// src/api.js

// Base URL for your backend.
export const API_BASE = "https://meetur-garden-thesis.onrender.com/api";
// Helper: read token from localStorage and build Authorization header
function getAuthHeaders() {
  const stored = localStorage.getItem("user");
  if (!stored) return {};

  try {
    const parsed = JSON.parse(stored);
    if (!parsed || !parsed.token) {
      return {};
    }

    return { Authorization: `Bearer ${parsed.token}` };
  } catch (err) {
    console.error("Failed to parse user from localStorage", err);
    return {};
  }
}

export const TwoFactorAPI = {
  // POST /api/user/2fa/generate
  generate: () => {
    return fetch(`${API_BASE}/user/2fa/generate`, {
      method: "POST",
      headers: { ...getAuthHeaders() },
    }).then(handle); // <-- Use your common handler!
  },

  // POST /api/user/2fa/verify
  verifySetup: (token) => {
    return fetch(`${API_BASE}/user/2fa/verify`, { // 🟢 FIXED URL HERE
      method: "POST",
      headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
      body: JSON.stringify({ token }),
    }).then(handle); // <-- Use your common handler!
  }
};

// Common response handler
async function handle(res) {
  const ct = res.headers.get("content-type") || "";

  if (!res.ok) {
    if (ct.includes("application/json")) {
      const data = await res.json().catch(() => ({}));
      const msg = data?.error || JSON.stringify(data) || `HTTP ${res.status}`;
      throw new Error(msg);
    } else {
      const text = await res.text();
      throw new Error(
        `Non JSON response (status ${res.status}): ${text.slice(0, 200)}`
      );
    }
  }

  if (ct.includes("application/json")) {
    return res.json();
  }

  return res.text();
}

// ---------------------------------------------------
// PLANTS API – all routes are protected by requireAuth
// ---------------------------------------------------

export const PlantsAPI = {
  // GET /api/plants
  // Optional: accept filters as query params later
  list: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    const url = qs ? `${API_BASE}/plants?${qs}` : `${API_BASE}/plants`;

    return fetch(url, {
      headers: {
        ...getAuthHeaders()
      }
    })
      .then(handle)
      .then((data) => {
        // Backend returns { count, plants }
        if (data && Array.isArray(data.plants)) {
          return data.plants;
        }
        // If backend ever changes to return a bare array, still work
        if (Array.isArray(data)) return data;
        return [];
      });

      
  },

  // GET /api/plants/:id
  get: (id) =>
    fetch(`${API_BASE}/plants/${id}`, {
      headers: {
        ...getAuthHeaders()
      }
    }).then(handle),

  // POST /api/plants
  create: (payload) =>
    fetch(`${API_BASE}/plants`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...getAuthHeaders()
      },
      body: JSON.stringify(payload)
    }).then(handle),

  // PATCH /api/plants/:id
  update: (id, payload) =>
    fetch(`${API_BASE}/plants/${id}`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        ...getAuthHeaders()
      },
      body: JSON.stringify(payload)
    }).then(handle),

  // DELETE /api/plants/:id
  delete: (id) =>
    fetch(`${API_BASE}/plants/${id}`, {
      method: "DELETE",
      headers: {
        ...getAuthHeaders()
      }
    }).then(handle),

    // Fetch unique families
    getFamilies: () => 
    fetch(`${API_BASE}/plants/families/unique`, { 
      headers: { ...getAuthHeaders() } 
    }).then(handle),
};

// ---------------------------------------------------
// GENERA API (optional helper if you use it later)
// ---------------------------------------------------

export const GeneraAPI = {
  // GET /api/genera
  list: () => fetch(`${API_BASE}/genera`).then(handle),

  // GET /api/genera/:name
  get: (name) =>
    fetch(`${API_BASE}/genera/${encodeURIComponent(name)}`).then(handle)
};

// ---------------------------------------------------
// WEATHER API
// ---------------------------------------------------

export const WeatherAPI = {
  // GET /api/weather?city=Manila or lat/lon
  get: (params) => {
    const queryString = new URLSearchParams(params).toString();
    return fetch(`${API_BASE}/weather?${queryString}`).then(handle);
  },

  // 🟢 FIXED: Securely calls YOUR backend instead of OpenWeatherMap directly
  getForecast: (params) => {
    const queryString = new URLSearchParams(params).toString();
    return fetch(`${API_BASE}/weather/forecast?${queryString}`).then(handle);
  },

  getMapConfig: () => fetch(`${API_BASE}/weather/config`).then(handle)
};

// ---------------------------------------------------
// GARDEN API (NEW - REQUIRED FOR THESIS)
// ---------------------------------------------------

export const GardenAPI = {
  // GET /api/garden - List user's plants
  list: () => 
    fetch(`${API_BASE}/garden`, { 
      headers: { ...getAuthHeaders() } 
    }).then(handle),
  
  // POST /api/garden - Add plant to garden
  add: (plant_id, nickname) => 
    fetch(`${API_BASE}/garden`, {
      method: 'POST',
      headers: {
        "Content-Type": "application/json",
        ...getAuthHeaders()
      },
      body: JSON.stringify({ plant_id, nickname })
    }).then(handle),

  // DELETE /api/garden/:id - Remove from garden
  // Note: 'id' here is the GARDEN ITEM ID, not the Plant ID
  remove: (id) => 
    fetch(`${API_BASE}/garden/${id}`, { 
      method: 'DELETE',
      headers: { ...getAuthHeaders() } 
    }).then(handle),

  // PATCH /api/garden/:id - Rename plant
  update: (id, payload) => 
    fetch(`${API_BASE}/garden/${id}`, {
      method: 'PATCH',
      headers: {
        "Content-Type": "application/json",
        ...getAuthHeaders()
      },
      body: JSON.stringify(payload)
    }).then(handle),
  // ------------------------------------

  // PATCH /api/garden/:id/action - Log care (water/sun)
  logAction: (id, action) => 
    fetch(`${API_BASE}/garden/${id}/action`, {
      method: 'PATCH',
      headers: {
        "Content-Type": "application/json",
        ...getAuthHeaders()
      },
      body: JSON.stringify({ action })
    }).then(handle)
};

// ---------------------------------------------------
// COMMUNITY BLOG API
// ---------------------------------------------------
export const BlogAPI = {
  // GET /api/blogs - Fetch the community feed
  list: () => 
    fetch(`${API_BASE}/blogs`, { 
      headers: { ...getAuthHeaders() } 
    }).then(handle),
  
  // POST /api/blogs - Create a new post
  create: (payload) => 
    fetch(`${API_BASE}/blogs`, {
      method: 'POST',
      headers: {
        "Content-Type": "application/json",
        ...getAuthHeaders()
      },
      body: JSON.stringify(payload)
    }).then(handle),

  // PATCH /api/blogs/:id/flag - Report a post
  flag: (id) => 
    fetch(`${API_BASE}/blogs/${id}/flag`, {
      method: 'PATCH',
      headers: { ...getAuthHeaders() }
    }).then(handle),

  // DELETE /api/blogs/:id - Delete a post (Admin/Author only)
  remove: (id) => 
    fetch(`${API_BASE}/blogs/${id}`, {
      method: 'DELETE',
      headers: { ...getAuthHeaders() }
    }).then(handle),

  // GET /api/blogs/admin/flagged - Admin moderation queue
  getFlagged: () => 
    fetch(`${API_BASE}/blogs/admin/flagged`, { 
      headers: { ...getAuthHeaders() } 
    }).then(handle),

  // POST /api/blogs/:id/reply - Add a reply to a post
  reply: (id, text) => 
    fetch(`${API_BASE}/blogs/${id}/reply`, {
      method: 'POST',
      headers: {
        "Content-Type": "application/json",
        ...getAuthHeaders()
      },
      body: JSON.stringify({ text })
    }).then(handle),
};

// ---------------------------------------------------
// USER / AUTH API
// ---------------------------------------------------

export const UserAPI = {
  // POST /api/user/forgot-password
  forgotPassword: (email) => 
    fetch(`${API_BASE}/user/forgot-password`, {
      method: 'POST',
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ email })
    }).then(handle),

  // POST /api/user/reset-password/:token
  resetPassword: (token, password) => 
    fetch(`${API_BASE}/user/reset-password/${token}`, {
      method: 'POST',
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ password })
    }).then(handle)
};
